import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, labelStyle, tdStyle, thStyle } from '../theme';
import DrugInteractionAlert from './DrugInteractionAlert';
import VoiceInput from './VoiceInput';
import { computeQty } from '../utils/rxQuantity';

// Stock badge palette — keyed on the string returned by /pharmacy/stock-check.
// Separate from the overall theme tokens so colors stay meaningful even when
// the main palette is tweaked (green = in-stock, red = out).
const STOCK_COLOR = {
  'in-stock':          { bg: '#d1fae5', fg: '#065f46', icon: '✓' },
  'low-stock':         { bg: '#fef3c7', fg: '#92400e', icon: '⚠' },
  'out-of-stock':      { bg: '#fee2e2', fg: '#991b1b', icon: '✗' },
  'not-in-catalog':    { bg: '#e0e7ff', fg: '#3730a3', icon: '?' },
  'alternatives-only': { bg: '#e0e7ff', fg: '#3730a3', icon: '?' },
};

/**
 * PatientVisitView — BestoSys-style single-page visit timeline.
 *
 * Shows all visits as expandable date cards. Each visit card contains:
 * Clinical Note, Treatment Plan, Treatment Done, Prescription, Instructions
 *
 * "+" button opens a new visit form with all sections together.
 *
 * Props:
 *   patientId, patientName, API_BASE
 *   treatmentTypes, instructionList, medicineLibrary
 *   chiefComplaintList, findingsList, investigationList, diagnosisList
 *   onRefresh — callback to refresh parent data
 */
const PatientVisitView = ({
  patientId, patientName, patientPhone, API_BASE,
  treatmentTypes, instructionList, medicineLibrary,
  chiefComplaintList, findingsList, investigationList, diagnosisList,
  onRefresh, whatsappTreatmentDone, whatsappTreatmentPlan,
  /* External controls — when the parent wants to drive "Add visit" itself.
     triggerNewVisit: counter — incrementing it opens the new-visit form.
     hideInternalAddButton: hides the built-in floating + button to avoid
     clashing with a parent-provided FAB. */
  triggerNewVisit,
  hideInternalAddButton,
}) => {
  const [visits, setVisits] = useState([]);
  const [showNewVisit, setShowNewVisit] = useState(false);
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [loading, setLoading] = useState(true);

  // New visit form state
  const [visitForm, setVisitForm] = useState({
    chiefComplaint: '', findings: '', investigation: '', diagnosis: '', notes: '',
    painScale: null, // 0-10 numeric pain rating; null = not asked
    treatmentName: '', toothNumber: '', price: 0, units: 1, status: 'planned',
    totalSittings: 1,
    medicines: [],
    instructions: [],
  });
  const [newMed, setNewMed] = useState({ name: '', dosage: '', duration: '', instructions: '' });
  const [saving, setSaving] = useState(false);
  const [stockMap, setStockMap] = useState({}); // { medName: { status, available, alternatives[] } }

  // Debounced stock-check on the current medicines list. Single bulk call
  // instead of one-per-row, and a 300ms guard stops thrashing as the
  // doctor types in the dosage/duration fields.
  useEffect(() => {
    if (!visitForm?.medicines?.length) { setStockMap({}); return; }
    const items = visitForm.medicines.map(m => {
      const { qty } = computeQty({ dosage: m.dosage, duration: m.duration });
      return { name: m.name, qty };
    });
    const t = setTimeout(() => {
      axios.post(`${API_BASE}/pharmacy/stock-check-bulk`, { items })
        .then(r => {
          const map = {};
          for (const row of (r.data?.results || [])) map[row.name] = row;
          setStockMap(map);
        })
        .catch(() => { /* pharmacy module optional — silently skip badge */ });
    }, 300);
    return () => clearTimeout(t);
  }, [visitForm?.medicines, API_BASE]);

  // Prescription templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/prescription-templates`).then(res => {
      setTemplates(res.data || []);
    }).catch(() => {});
  }, [API_BASE]);

  // Parent can trigger "show new visit" by incrementing `triggerNewVisit`.
  useEffect(() => {
    if (triggerNewVisit) setShowNewVisit(true);
  }, [triggerNewVisit]);

  // Fetch all data and group by date
  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const [rxRes, txRes, billRes] = await Promise.all([
        axios.get(`${API_BASE}/prescriptions/patient/${patientId}`),
        axios.get(`${API_BASE}/treatments/patient/${patientId}`),
        axios.get(`${API_BASE}/bills/patient/${patientId}`),
      ]);

      // Group by date
      const dateMap = {};

      rxRes.data.forEach(rx => {
        const dateKey = new Date(rx.date).toISOString().split('T')[0];
        if (!dateMap[dateKey]) dateMap[dateKey] = { date: dateKey, prescriptions: [], treatments: [], bills: [] };
        dateMap[dateKey].prescriptions.push(rx);
      });

      txRes.data.forEach(tx => {
        const dateKey = new Date(tx.doneDate || tx.plannedDate || tx.createdAt).toISOString().split('T')[0];
        if (!dateMap[dateKey]) dateMap[dateKey] = { date: dateKey, prescriptions: [], treatments: [], bills: [] };
        dateMap[dateKey].treatments.push(tx);
      });

      billRes.data.forEach(b => {
        const dateKey = new Date(b.date).toISOString().split('T')[0];
        if (!dateMap[dateKey]) dateMap[dateKey] = { date: dateKey, prescriptions: [], treatments: [], bills: [] };
        dateMap[dateKey].bills.push(b);
      });

      const sorted = Object.values(dateMap).sort((a, b) => b.date.localeCompare(a.date));
      setVisits(sorted);
      if (sorted.length > 0 && !expandedVisit) setExpandedVisit(sorted[0].date);
    } catch (e) {}
    setLoading(false);
  }, [patientId, API_BASE]); // eslint-disable-line

  useEffect(() => { fetchVisits(); }, [fetchVisits]);

  const fmtDate = d => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtINR = n => `₹${(n || 0).toLocaleString('en-IN')}`;

  // Treatment type selection
  const onTTSelect = (name) => {
    const tt = (treatmentTypes || []).find(t => t.name === name);
    if (tt) {
      setVisitForm(f => ({ ...f, treatmentName: name, price: tt.defaultPrice || 0, units: tt.defaultUnits || 1 }));
      if (tt.linkedMedicines?.length > 0) setVisitForm(f => ({ ...f, medicines: tt.linkedMedicines.map(m => ({ ...m })) }));
      if (tt.linkedInstructions?.length > 0) setVisitForm(f => ({ ...f, instructions: [...tt.linkedInstructions] }));
    } else {
      setVisitForm(f => ({ ...f, treatmentName: name }));
    }
  };

  const addMedicine = () => {
    if (!newMed.name) return;
    setVisitForm(f => ({ ...f, medicines: [...f.medicines, { ...newMed }] }));
    setNewMed({ name: '', dosage: '', duration: '', instructions: '' });
  };

  const removeMedicine = (idx) => {
    setVisitForm(f => ({ ...f, medicines: f.medicines.filter((_, i) => i !== idx) }));
  };

  const toggleInstruction = (label) => {
    setVisitForm(f => ({
      ...f,
      instructions: f.instructions.includes(label)
        ? f.instructions.filter(x => x !== label)
        : [...f.instructions, label],
    }));
  };

  const loadTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId) return;
    const tpl = templates.find(t => t._id === templateId);
    if (tpl) {
      setVisitForm(f => ({
        ...f,
        chiefComplaint: tpl.chiefComplaint || f.chiefComplaint,
        findings: tpl.findings || f.findings,
        investigation: tpl.investigation || f.investigation,
        diagnosis: tpl.diagnosis || f.diagnosis,
        medicines: tpl.medicines?.length > 0 ? tpl.medicines.map(m => ({ ...m })) : f.medicines,
        instructions: tpl.instructions?.length > 0 ? [...tpl.instructions] : f.instructions,
      }));
    }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Enter a name for this template:');
    if (!name || !name.trim()) return;
    try {
      const payload = {
        name: name.trim(),
        chiefComplaint: visitForm.chiefComplaint,
        findings: visitForm.findings,
        investigation: visitForm.investigation,
        diagnosis: visitForm.diagnosis,
        medicines: visitForm.medicines,
        instructions: visitForm.instructions,
      };
      const res = await axios.post(`${API_BASE}/prescription-templates`, payload);
      setTemplates(prev => [...prev, res.data]);
      alert('Template saved: ' + name.trim());
    } catch (e) {
      alert('Failed to save template: ' + (e.response?.data?.error || e.message));
    }
  };

  // Save complete visit (prescription + treatment + bill in one shot)
  /**
   * Open all plan-time consents in a single print window.
   * Each consent's HTML document is already standalone; we strip their
   * wrapping <html>/<body> tags and concat their <div class="page">
   * blocks so they print as one packet.
   */
  const openConsentsForPrint = (consents) => {
    if (!consents || consents.length === 0) return;
    // Pull the <style> from the first consent (all identical).
    const firstMatch = consents[0].consentHtml.match(/<style>([\s\S]*?)<\/style>/);
    const styles = firstMatch ? firstMatch[1] : '';
    const pages = consents.map(c => {
      const body = c.consentHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/);
      return body ? body[1] : c.consentHtml;
    }).join('');
    const title = `Consent Forms — ${consents[0]?.patientName || 'Patient'}`;
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) { alert('Please allow pop-ups to auto-print consent forms'); return; }
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title><style>${styles}</style></head><body>${pages}<script>window.onload=function(){setTimeout(function(){window.print();},350)}<\/script></body></html>`);
    w.document.close();
  };

  const handleSaveVisit = async () => {
    if (!visitForm.chiefComplaint && !visitForm.treatmentName) {
      return alert('Enter chief complaint or select a treatment');
    }
    setSaving(true);
    try {
      // 1. Save prescription
      if (visitForm.chiefComplaint || visitForm.diagnosis || visitForm.medicines.length > 0) {
        await axios.post(`${API_BASE}/prescriptions/add`, {
          patientId,
          chiefComplaint: visitForm.chiefComplaint,
          findings: visitForm.findings,
          investigation: visitForm.investigation,
          diagnosis: visitForm.diagnosis,
          painScale: visitForm.painScale,
          medicines: visitForm.medicines,
          notes: visitForm.notes,
        });
      }

      // 2. Save treatment (if selected)
      if (visitForm.treatmentName) {
        const totalAmount = (visitForm.price * visitForm.units);
        const txData = {
          patientId,
          treatmentName: visitForm.treatmentName,
          toothNumber: visitForm.toothNumber,
          price: visitForm.price,
          units: visitForm.units,
          totalAmount,
          status: visitForm.status,
          totalSittings: visitForm.totalSittings || 1,
          currentSitting: 1,
          medicines: visitForm.medicines,
          instructions: visitForm.instructions,
        };

        if (visitForm.status === 'done') {
          txData.doneDate = new Date();
          // Auto-create bill + prescription + consent(s)
          await axios.post(`${API_BASE}/treatments/done`, txData);
        } else {
          // Plan → server auto-generates the appropriate consent forms.
          // Open them in a single print window so the patient can sign BEFORE
          // the procedure starts.
          const planRes = await axios.post(`${API_BASE}/treatments/plan`, txData);
          const planConsents = (planRes.data?.consents || []).filter(c => c.consentHtml);
          if (planConsents.length > 0) {
            try { openConsentsForPrint(planConsents); } catch (e) { /* popup blocked */ }
          }
        }
      }

      // Auto WhatsApp based on treatment status
      if (patientPhone && visitForm.treatmentName) {
        if (visitForm.status === 'done' && whatsappTreatmentDone) {
          whatsappTreatmentDone(visitForm.treatmentName, visitForm.toothNumber, visitForm.medicines, visitForm.instructions, visitForm.price * visitForm.units);
        } else if (visitForm.status === 'planned' && whatsappTreatmentPlan) {
          whatsappTreatmentPlan(visitForm.treatmentName, visitForm.toothNumber, visitForm.price * visitForm.units);
        }
      }

      // Reset form
      setVisitForm({
        chiefComplaint: '', findings: '', investigation: '', diagnosis: '', notes: '',
        treatmentName: '', toothNumber: '', price: 0, units: 1, status: 'planned',
        totalSittings: 1,
        medicines: [], instructions: [],
      });
      setShowNewVisit(false);
      fetchVisits();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  // VoiceInput callback — append transcribed speech to a visitForm field.
  const appendToField = (field, text) => setVisitForm(f => ({
    ...f, [field]: f[field] ? `${f[field]} ${text}` : text,
  }));

  // Preset selector — typeable input with autocomplete suggestions + already-selected chips.
  // Replaces the old vanilla <select> which was awkward (one-at-a-time, hidden options).
  // Now: see selected items as removable chips, type to search the master list, click
  // a suggestion to append. Each value is comma-separated under the hood.
  const PresetSelect = ({ label, list, value, onChange, placeholder }) => {
    const [search, setSearch] = React.useState('');
    const [focused, setFocused] = React.useState(false);
    const isEmpty = !list || list.length === 0;
    const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
    const matches = isEmpty ? [] : (list || []).filter(item => {
      if (selected.includes(item.label)) return false;
      if (!search) return true;
      return item.label.toLowerCase().includes(search.toLowerCase());
    }).slice(0, 8);

    const addItem = (label) => {
      const next = selected.includes(label) ? selected : [...selected, label];
      onChange(next.join(', '));
      setSearch('');
    };
    const removeItem = (label) => {
      onChange(selected.filter(s => s !== label).join(', '));
    };

    return (
      <div style={{ marginBottom: 14 }}>
        <label style={{ ...labelStyle, color: 'var(--color-text)' }}>{label}</label>
        {/* Selected chips — theme-aware so dark phases stay readable */}
        {selected.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
            {selected.map((s, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 14, fontSize: 12, fontWeight: 600,
                backgroundColor: 'var(--color-accent-soft)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-accent-border)',
              }}>
                {s}
                <span onClick={() => removeItem(s)} style={{ cursor: 'pointer', color: 'var(--color-danger)', fontWeight: 700, fontSize: 14, lineHeight: 1 }}>×</span>
              </span>
            ))}
          </div>
        )}
        {/* Search input + free text */}
        <div style={{ position: 'relative' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={e => {
              if (e.key === 'Enter' && search.trim()) {
                e.preventDefault();
                addItem(search.trim());
              }
            }}
            placeholder={isEmpty ? 'Type and Enter to add (no presets loaded)' : `Search ${label.toLowerCase()} or type custom + Enter`}
            style={{ ...inputStyle, width: '100%' }}
          />
          {/* Suggestions dropdown — uses bg-card/text so it inverts in night mode */}
          {focused && matches.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text)',
              borderRadius: 6, maxHeight: 220, overflowY: 'auto', marginTop: 2,
              boxShadow: 'var(--color-shadow)',
            }}>
              {matches.map((item, i) => (
                <div key={item._id} onMouseDown={() => addItem(item.label)}
                  style={{
                    padding: '7px 12px', cursor: 'pointer', fontSize: 13,
                    color: 'var(--color-text)',
                    borderBottom: i < matches.length - 1 ? '1px solid var(--color-border)' : 'none',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-accent-soft)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  + {item.label}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Inline hint */}
        {!isEmpty && (
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
            Type to search, click suggestion to add. Custom text? Type and press Enter.
          </div>
        )}
      </div>
    );
  };

  // Pain Scale (0–10) — color-coded clickable buttons. Standard numeric rating
  // scale: 0 = no pain, 1–3 mild (green), 4–6 moderate (yellow), 7–10 severe (red).
  const PainScale = ({ value, onChange }) => {
    const colorFor = (n) => {
      if (n === null || n === undefined) return '#e5e7eb';
      if (n === 0) return '#16a34a';
      if (n <= 3) return '#84cc16';
      if (n <= 6) return '#f59e0b';
      return '#dc2626';
    };
    const labelFor = (n) => {
      if (n === 0) return 'No pain';
      if (n <= 3) return 'Mild';
      if (n <= 6) return 'Moderate';
      return 'Severe';
    };
    return (
      <div style={{ marginBottom: 14 }}>
        <label style={{ ...labelStyle, color: 'var(--color-text)' }}>
          Pain Scale (0–10)
          {value !== null && value !== undefined && (
            <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
              background: colorFor(value), color: 'white' }}>
              {value} — {labelFor(value)}
            </span>
          )}
        </label>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => {
            const isSelected = value === n;
            return (
              <button key={n} type="button"
                onClick={() => onChange(isSelected ? null : n)}
                style={{
                  width: 36, height: 36, borderRadius: 8,
                  border: isSelected ? `2px solid ${colorFor(n)}` : '1px solid var(--color-border)',
                  background: isSelected ? colorFor(n) : 'var(--color-bg-inset)',
                  color: isSelected ? 'white' : 'var(--color-text)',
                  fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  transition: '0.15s',
                }}>
                {n}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4, padding: '0 4px' }}>
          <span>No pain</span>
          <span>Mild</span>
          <span>Moderate</span>
          <span>Severe</span>
        </div>
      </div>
    );
  };

  // If all four preset lists are empty, the clinic hasn't been seeded yet.
  // Render a one-click "Load defaults" banner inline in the Rx form so the
  // doctor doesn't have to leave to Settings or Super Admin.
  const allPresetsEmpty =
    (!chiefComplaintList || chiefComplaintList.length === 0) &&
    (!findingsList || findingsList.length === 0) &&
    (!investigationList || investigationList.length === 0) &&
    (!diagnosisList || diagnosisList.length === 0);

  const loadPresetDefaults = async () => {
    if (!window.confirm('Load default presets for this clinic?\n\n~390 dental defaults will be added (Chief Complaints, Findings, Investigations, Diagnoses, Treatment Types, etc.). Existing items skipped.')) return;
    try {
      const r = await axios.post(`${API_BASE}/settings/seed-defaults`);
      alert(`✅ ${r.data.message}\n\nReloading to show options...`);
      window.location.reload();
    } catch (e) {
      alert('Seed failed: ' + (e.response?.data?.error || e.message));
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: 10, flexWrap:'wrap' }}>
        <h3 style={{ margin: 0, color: theme.navy, fontSize: '1.05rem' }}>
          Visit History ({visits.length} visits)
        </h3>
        {patientId && (
          <button
            onClick={async () => {
              if (!window.confirm(`Send secure portal link via WhatsApp to ${patientName || 'patient'}?\n\nThe link lets them view their appointments, treatments, and bills.`)) return;
              try {
                const r = await axios.post(`${API_BASE}/patient-portal/request-link`, { patientId });
                alert(r.data.whatsappSent
                  ? `✓ Link sent!\n\nValid till: ${new Date(r.data.expiresAt).toLocaleDateString('en-IN')}`
                  : `Link generated but WhatsApp delivery failed.\n\nLink: ${r.data.link}\n(Copy and share manually.)`);
              } catch (e) {
                alert('Failed: ' + (e.response?.data?.error || e.message));
              }
            }}
            style={{ padding:'8px 14px', background:'#25d366', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13, display:'flex', gap:6, alignItems:'center' }}
            title="Generates a 7-day portal link and sends via WhatsApp">
            📲 Send Portal Link
          </button>
        )}
      </div>

      {/* Floating + New Visit button — hidden when parent provides its own FAB */}
      {!hideInternalAddButton && (
        <button onClick={() => setShowNewVisit(!showNewVisit)}
          style={{
            position: 'fixed', bottom: '30px', right: '36px', zIndex: 999,
            padding: showNewVisit ? '12px 26px' : '14px 30px',
            background: showNewVisit ? '#6c757d' : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
            color: 'white', border: 'none', borderRadius: '50px',
            cursor: 'pointer', fontWeight: '800', fontSize: '1rem',
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            transition: 'all 0.2s ease',
            letterSpacing: '0.02em',
          }}>
          {showNewVisit ? '✕ Cancel' : '+ New Visit'}
        </button>
      )}

      {/* ── NEW VISIT FORM ── */}
      {showNewVisit && (
        <div style={{ ...cardStyle, marginBottom: '18px', borderTop: `4px solid ${theme.primary}`, backgroundColor: 'var(--color-bg-card)' }}>
          <h4 style={{ margin: '0 0 14px', color: 'var(--color-text)' }}>New Visit — {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</h4>

          {/* Clinical Note Section */}
          <div style={{ padding: '12px', backgroundColor: 'var(--color-bg-inset)', borderRadius: '8px', marginBottom: '14px', borderLeft: `4px solid ${theme.danger}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h5 style={{ margin: 0, color: theme.navy }}>Clinical Note</h5>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select value={selectedTemplate} onChange={e => loadTemplate(e.target.value)}
                  style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.82rem', fontWeight: '600', color: theme.navy, cursor: 'pointer', minWidth: '160px' }}>
                  <option value="">Load Template...</option>
                  {templates.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
                <button onClick={saveAsTemplate}
                  style={{ padding: '5px 12px', borderRadius: '6px', border: `1px solid ${theme.info}`, backgroundColor: 'white', color: theme.info,
                    fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Save as Template
                </button>
              </div>
            </div>
            {allPresetsEmpty && (
              <div style={{
                padding: '10px 14px', marginBottom: 12, borderRadius: 8,
                backgroundColor: '#fff7ed', border: '1px solid #fdba74',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ fontSize: '0.84rem', color: '#9a3412' }}>
                  <strong>No clinic presets loaded yet.</strong> Click below to load 390+ dental defaults (Chief Complaints, Findings, Investigations, Diagnoses, Treatments).
                </div>
                <button onClick={loadPresetDefaults}
                  style={{ padding: '7px 14px', backgroundColor: '#ea580c', color: 'white', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  🌱 Load defaults
                </button>
              </div>
            )}
            {/* Voice dictation toolbar — Web Speech API, no external service. */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
              <span>🎙️ Voice fill:</span>
              <VoiceInput onTranscript={t => appendToField('chiefComplaint', t)} title="Chief Complaint"/>
              <VoiceInput onTranscript={t => appendToField('findings', t)} title="Findings"/>
              <VoiceInput onTranscript={t => appendToField('investigation', t)} title="Investigation"/>
              <VoiceInput onTranscript={t => appendToField('diagnosis', t)} title="Diagnosis"/>
              <span style={{ marginLeft: 6 }}>← click mic + speak</span>
            </div>
            <PresetSelect label="Chief Complaint" list={chiefComplaintList} value={visitForm.chiefComplaint}
              onChange={v => setVisitForm(f => ({ ...f, chiefComplaint: v }))} placeholder="Type or select..." />
            <PainScale value={visitForm.painScale} onChange={n => setVisitForm(f => ({ ...f, painScale: n }))} />
            <PresetSelect label="Findings" list={findingsList} value={visitForm.findings}
              onChange={v => setVisitForm(f => ({ ...f, findings: v }))} placeholder="Clinical findings..." />
            <PresetSelect label="Investigation" list={investigationList} value={visitForm.investigation}
              onChange={v => setVisitForm(f => ({ ...f, investigation: v }))} placeholder="Tests ordered..." />
            <PresetSelect label="Diagnosis" list={diagnosisList} value={visitForm.diagnosis}
              onChange={v => setVisitForm(f => ({ ...f, diagnosis: v }))} placeholder="Diagnosis..." />
          </div>

          {/* Treatment Section */}
          <div style={{ padding: '12px', backgroundColor: 'var(--color-bg-inset)', borderRadius: '8px', marginBottom: '14px', borderLeft: `4px solid ${theme.warning}` }}>
            <h5 style={{ margin: '0 0 10px', color: theme.navy }}>Treatment</h5>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              <div>
                <label style={labelStyle}>Treatment</label>
                <select value={visitForm.treatmentName} onChange={e => onTTSelect(e.target.value)} style={inputStyle}>
                  <option value="">Select treatment...</option>
                  {(treatmentTypes || []).map(tt => <option key={tt._id} value={tt.name}>{tt.name}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Tooth #</label><input value={visitForm.toothNumber} onChange={e => setVisitForm(f => ({ ...f, toothNumber: e.target.value }))} placeholder="#14" style={inputStyle} /></div>
              <div><label style={labelStyle}>Price ₹</label><input type="number" value={visitForm.price} onChange={e => setVisitForm(f => ({ ...f, price: Number(e.target.value) }))} style={inputStyle} /></div>
              <div><label style={labelStyle}>Units</label><input type="number" value={visitForm.units} onChange={e => setVisitForm(f => ({ ...f, units: Number(e.target.value) }))} style={inputStyle} /></div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={visitForm.status} onChange={e => setVisitForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                  <option value="planned">Planned</option>
                  <option value="done">Done</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
              <div style={{ fontSize: '0.85rem', color: theme.navy, fontWeight: '600' }}>
                Total: {fmtINR(visitForm.price * visitForm.units)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ ...labelStyle, margin: 0 }}>Total Sittings</label>
                <input type="number" min="1" value={visitForm.totalSittings}
                  onChange={e => setVisitForm(f => ({ ...f, totalSittings: Math.max(1, Number(e.target.value)) }))}
                  style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                <span style={{ fontSize: '0.82rem', color: theme.textMuted, fontWeight: '600' }}>
                  Sitting 1 of {visitForm.totalSittings}
                </span>
              </div>
            </div>
          </div>

          {/* Prescription / Medicines Section */}
          <div style={{ padding: '12px', backgroundColor: 'var(--color-bg-inset)', borderRadius: '8px', marginBottom: '14px', borderLeft: `4px solid ${theme.success}` }}>
            <h5 style={{ margin: '0 0 10px', color: theme.navy }}>Medicines ({visitForm.medicines.length})</h5>
            {visitForm.medicines.map((m, i) => {
              const { qty, rationale } = computeQty({ dosage: m.dosage, duration: m.duration });
              const stock = stockMap[m.name];
              const pal = stock ? STOCK_COLOR[stock.status] : null;
              const stockTitle = stock
                ? `${stock.status} — ${stock.available} in stock, ${stock.requested} needed${stock.alternatives?.length ? `. Alternatives: ${stock.alternatives.map(a => a.name).join(', ')}` : ''}`
                : 'Stock check pending';
              return (
                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px', padding: '4px 8px', backgroundColor: 'white', borderRadius: '6px', fontSize: '0.82rem' }}>
                  <span style={{ flex: 1, fontWeight: '600' }}>{m.name}</span>
                  <span style={{ color: theme.textMuted }}>{m.dosage} • {m.duration}</span>
                  <span title={rationale} style={{ padding: '2px 8px', backgroundColor: '#ecfdf5', color: '#065f46', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700' }}>
                    Total: {qty}
                  </span>
                  {pal && (
                    <span title={stockTitle} style={{ padding: '2px 8px', backgroundColor: pal.bg, color: pal.fg, borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700' }}>
                      {pal.icon} {stock.available}
                    </span>
                  )}
                  <span onClick={() => removeMedicine(i)} style={{ cursor: 'pointer', color: theme.danger, fontWeight: '700' }}>×</span>
                </div>
              );
            })}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr auto', gap: '6px', marginTop: '8px' }}>
              <select value={newMed.name} onChange={e => {
                const lib = (medicineLibrary || []).map(item => { try { return { ...JSON.parse(item.label), _id: item._id }; } catch (ex) { return { name: item.label, _id: item._id }; } });
                const found = lib.find(m => m.name === e.target.value);
                if (found) setNewMed({ name: found.name, dosage: found.dosage || '', duration: found.duration || '', instructions: found.instructions || '' });
                else setNewMed(f => ({ ...f, name: e.target.value }));
              }} style={inputStyle}>
                <option value="">Select medicine...</option>
                {(medicineLibrary || []).map(item => { let med; try { med = JSON.parse(item.label); } catch (e) { med = { name: item.label }; } return <option key={item._id} value={med.name}>{med.name}</option>; })}
              </select>
              <input value={newMed.dosage} onChange={e => setNewMed(f => ({ ...f, dosage: e.target.value }))} placeholder="Dosage" style={inputStyle} />
              <input value={newMed.duration} onChange={e => setNewMed(f => ({ ...f, duration: e.target.value }))} placeholder="Duration" style={inputStyle} />
              <input value={newMed.instructions} onChange={e => setNewMed(f => ({ ...f, instructions: e.target.value }))} placeholder="Instructions" style={inputStyle} />
              <button onClick={addMedicine} style={{ padding: '8px 14px', backgroundColor: theme.success, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700' }}>+</button>
            </div>
          </div>

          {/* Drug Interaction Alert */}
          {visitForm.medicines.length > 0 && (
            <DrugInteractionAlert
              medicines={visitForm.medicines.map(m => m.name)}
              patientMedicines={[]}
              isPregnant={false}
              API_BASE={API_BASE}
            />
          )}

          {/* Instructions Section */}
          <div style={{ padding: '12px', backgroundColor: 'var(--color-bg-inset)', borderRadius: '8px', marginBottom: '14px', borderLeft: `4px solid ${theme.info}` }}>
            <h5 style={{ margin: '0 0 10px', color: theme.navy }}>Post-Treatment Instructions</h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {(instructionList || []).map(item => (
                <button key={item._id} onClick={() => toggleInstruction(item.label)}
                  style={{
                    padding: '5px 12px', borderRadius: '14px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer',
                    border: `1px solid ${theme.info}`,
                    backgroundColor: visitForm.instructions.includes(item.label) ? theme.info : 'white',
                    color: visitForm.instructions.includes(item.label) ? 'white' : theme.info,
                  }}>
                  {visitForm.instructions.includes(item.label) ? '✓ ' : ''}{item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>Additional Notes</label>
            <input value={visitForm.notes} onChange={e => setVisitForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional notes..." style={inputStyle} />
          </div>

          {/* Save */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowNewVisit(false)} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
            <button onClick={handleSaveVisit} disabled={saving}
              style={{ padding: '10px 28px', backgroundColor: theme.success, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.95rem' }}>
              {saving ? 'Saving...' : '✓ Save Visit'}
            </button>
          </div>
        </div>
      )}

      {/* ── VISIT TIMELINE ── */}
      {loading ? <p style={{ color: theme.textMuted }}>Loading visits...</p> :
        visits.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#bbb', backgroundColor: '#fafafa', borderRadius: '10px' }}>
            No visits recorded yet — click "+ New Visit" to start
          </div>
        ) : visits.map(visit => {
          const isExpanded = expandedVisit === visit.date;
          const totalBilled = visit.bills.reduce((s, b) => s + (b.totalAmount || 0), 0);
          const plannedCount = visit.treatments.filter(t => t.status === 'planned').length;
          const doneCount = visit.treatments.filter(t => t.status === 'done').length;

          return (
            <div key={visit.date} style={{ marginBottom: '8px' }}>
              {/* Date header — click to expand/collapse */}
              <div onClick={() => setExpandedVisit(isExpanded ? null : visit.date)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', cursor: 'pointer',
                  backgroundColor: isExpanded ? theme.primaryLight : theme.surface,
                  border: `1px solid ${isExpanded ? theme.primary : theme.border}`,
                  borderRadius: isExpanded ? '10px 10px 0 0' : '10px',
                  transition: 'all 0.15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ color: isExpanded ? theme.primaryDark : theme.navy, fontWeight: '700' }}>{fmtDate(visit.date)}</span>
                  {doneCount > 0 && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: '#d1fae5', color: '#065f46' }}>✅ {doneCount} done</span>}
                  {plannedCount > 0 && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: '#fef3c7', color: '#92400e' }}>💉 {plannedCount} planned</span>}
                  {visit.prescriptions.length > 0 && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: '#dbeafe', color: '#1e40af' }}>📋 Rx</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {totalBilled > 0 && <span style={{ fontWeight: '700', color: theme.success }}>{fmtINR(totalBilled)}</span>}
                  <span style={{ color: theme.textSubtle }}>{isExpanded ? '▼' : '▶'}</span>
                </div>
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{ border: `1px solid ${theme.primary}`, borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '16px', backgroundColor: 'white' }}>

                  {/* Clinical Notes */}
                  {visit.prescriptions.map(rx => (
                    <div key={rx._id} style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: '#fef2f2', borderRadius: '8px', borderLeft: `4px solid ${theme.danger}` }}>
                      <div style={{ fontSize: '0.72rem', color: theme.danger, fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>Clinical Note</div>
                      {rx.clinicalNotes?.length > 0 && (
                        <div style={{ marginBottom: '6px' }}>
                          {rx.clinicalNotes.map((cn, i) => (
                            <span key={i} style={{ display: 'inline-block', backgroundColor: '#e8f4fd', color: theme.primaryDark, borderRadius: '4px', padding: '2px 8px', fontSize: '0.78rem', marginRight: '4px' }}>
                              #{cn.toothNumber}{cn.surfaces?.length > 0 && ` [${cn.surfaces.join(',')}]`}
                            </span>
                          ))}
                        </div>
                      )}
                      {rx.chiefComplaint && <div style={{ fontSize: '0.82rem', marginBottom: '3px' }}><strong>CC:</strong> {rx.chiefComplaint}</div>}
                      {rx.findings && <div style={{ fontSize: '0.82rem', marginBottom: '3px' }}><strong>Findings:</strong> {rx.findings}</div>}
                      {rx.investigation && <div style={{ fontSize: '0.82rem', marginBottom: '3px' }}><strong>Investigation:</strong> {rx.investigation}</div>}
                      {rx.diagnosis && <div style={{ fontSize: '0.82rem', marginBottom: '3px' }}><strong>Diagnosis:</strong> {rx.diagnosis}</div>}
                      {rx.medicines?.length > 0 && (
                        <div style={{ marginTop: '6px' }}>
                          <span style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: '700' }}>MEDICINES: </span>
                          {rx.medicines.map((m, i) => (
                            <span key={i} style={{ fontSize: '0.78rem', marginRight: '8px' }}>💊 {m.name} {m.dosage && `(${m.dosage})`}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Treatments */}
                  {visit.treatments.map(tx => (
                    <div key={tx._id} style={{
                      marginBottom: '8px', padding: '10px 12px', borderRadius: '8px',
                      backgroundColor: tx.status === 'done' ? '#f0fdf4' : '#fffbeb',
                      borderLeft: `4px solid ${tx.status === 'done' ? theme.success : theme.warning}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontSize: '0.72rem', color: tx.status === 'done' ? theme.success : theme.warning, fontWeight: '700', textTransform: 'uppercase' }}>
                            {tx.status === 'done' ? '✅ Treatment Done' : '💉 Treatment Plan'}
                          </span>
                          <div style={{ fontWeight: '600', color: theme.navy, fontSize: '0.9rem', marginTop: '2px' }}>
                            {tx.treatmentName}{tx.toothNumber ? ` — Tooth #${tx.toothNumber}` : ''}
                          </div>
                          {tx.totalSittings > 1 && (
                            <div style={{ fontSize: '0.72rem', color: '#6d28d9', fontWeight: '600', marginTop: '1px' }}>
                              Sitting {tx.currentSitting || 1} of {tx.totalSittings}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: '700', color: tx.status === 'done' ? theme.success : theme.navy }}>
                              {fmtINR(tx.totalAmount)}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>
                              {fmtINR(tx.price)} × {tx.units}
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {tx.status === 'planned' && (
                              <button onClick={async () => {
                                if (!window.confirm(`Mark "${tx.treatmentName}" as Done?`)) return;
                                try {
                                  // Carry the plan's existing clinical data through — otherwise
                                  // the backend resets medicines/instructions to empty arrays.
                                  await axios.put(`${API_BASE}/treatments/${tx._id}/done`, {
                                    medicines:    tx.medicines    || [],
                                    instructions: tx.instructions || [],
                                    price:        tx.price,
                                    units:        tx.units,
                                    discount:     tx.discount,
                                    gst:          tx.gst,
                                    gstPercent:   tx.gstPercent,
                                    totalAmount:  tx.totalAmount,
                                    notes:        tx.notes,
                                  });
                                  fetchVisits();
                                  if (onRefresh) onRefresh();
                                } catch(e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
                              }}
                                style={{ padding: '3px 8px', backgroundColor: theme.success, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '700' }}>
                                ✓ Done
                              </button>
                            )}
                            <button onClick={async () => {
                              if (!window.confirm(`Delete this treatment?`)) return;
                              try { await axios.delete(`${API_BASE}/treatments/${tx._id}`); fetchVisits(); if (onRefresh) onRefresh(); } catch(e) { alert('Failed'); }
                            }}
                              style={{ padding: '3px 8px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '600' }}>
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                      {tx.instructions?.length > 0 && (
                        <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {tx.instructions.map((ins, i) => (
                            <span key={i} style={{ padding: '2px 8px', backgroundColor: '#dbeafe', borderRadius: '10px', fontSize: '0.7rem', color: '#1e40af' }}>{ins}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Bills */}
                  {visit.bills.map(b => (
                    <div key={b._id} style={{ marginBottom: '8px', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', borderLeft: `4px solid ${theme.navy}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: '700', textTransform: 'uppercase' }}>🧾 Invoice</span>
                        <span style={{ marginLeft: '8px', fontSize: '0.82rem', fontWeight: '600', color: theme.navy }}>{b.invoiceNo || ''}</span>
                        <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: theme.textMuted }}>{(b.items||[]).map(i=>i.description).join(', ').substring(0,40)}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: '700', color: theme.success }}>{fmtINR(b.totalAmount)}</span>
                        <button onClick={() => {
                          const w = window.open('','_blank');
                          const oneBill = `<div style="padding:8px 10px;border:1px solid #333">
                            <div style="text-align:center;border-bottom:2px solid #333;padding-bottom:8px;margin-bottom:8px">
                              <div style="font-size:11px;font-weight:bold;letter-spacing:2px">INVOICE</div>
                              <div style="font-size:13px;font-weight:bold;margin-top:4px">SIDDHARTH DENTAL HOSPITAL</div>
                              <div style="font-size:8px;color:#555">Near Siddharth Computer, Fuliya Gate Road, Shahpura, Rajasthan 311404 | Ph: 9685058003</div>
                            </div>
                            <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:9px">
                              <div><strong>Patient:</strong> ${patientName}<br><strong>Date:</strong> ${new Date(b.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
                              <div style="text-align:right"><strong>Invoice #:</strong> ${b.invoiceNo||''}</div>
                            </div>
                            <table>
                              <thead><tr><th>#</th><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
                              <tbody>${(b.items||[]).map((it,i)=>`<tr><td>${i+1}</td><td style="font-weight:600">${it.description}</td><td style="text-align:right;font-weight:600">₹${(it.amount||0).toLocaleString('en-IN')}</td></tr>`).join('')}
                              <tr style="font-weight:bold;background:#fafafa"><td colspan="2" style="text-align:right">Total</td><td style="text-align:right;font-size:10px">₹${(b.totalAmount||0).toLocaleString('en-IN')}</td></tr></tbody>
                            </table>
                            <div style="text-align:right;font-size:11px;font-weight:bold;margin-top:6px">Amount Payable: ₹${(b.totalAmount||0).toLocaleString('en-IN')}</div>
                            <div style="display:flex;justify-content:space-between;margin-top:20px">
                              <div style="border-top:1px solid #333;margin-top:20px;padding-top:3px;font-size:8px;text-align:center;width:130px">Patient Signature</div>
                              <div style="border-top:1px solid #333;margin-top:20px;padding-top:3px;font-size:8px;text-align:center;width:130px">Authorized Signatory</div>
                            </div>
                          </div>`;
                          w.document.write(`<html><head><title>Invoice ${b.invoiceNo||''}</title>
                            <style>@page{size:A4;margin:10mm}*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial;font-size:9px;color:#222;line-height:1.3}
                            table{width:100%;border-collapse:collapse;margin:6px 0}th{background:#f5f5f5;border:1px solid #ccc;padding:4px 6px;text-align:left;font-size:8px;text-transform:uppercase}
                            td{border:1px solid #ccc;padding:3px 6px;font-size:9px}
                            .copy-label{text-align:right;font-size:8px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-bottom:2px}
                            @media print{body{-webkit-print-color-adjust:exact}}</style></head><body>
                            <div class="copy-label">ORIGINAL</div>${oneBill}
                            ${Number(localStorage.getItem('dentalBillCopies')||'1')>=2?(()=>{const gap=Number(localStorage.getItem('dentalBillGap')||'15');return `<div style="height:${gap}mm;display:flex;align-items:center"><div style="flex:1;border-top:1px dashed #999"></div><span style="padding:0 8px;font-size:7px;color:#aaa">✂ CUT HERE</span><div style="flex:1;border-top:1px dashed #999"></div></div><div class="copy-label">DUPLICATE</div>${oneBill}`;})():''}
                            <script>window.onload=function(){window.print();window.close();}<\/script></body></html>`);
                          w.document.close();
                        }}
                          style={{ padding: '3px 8px', backgroundColor: theme.primary, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '600' }}>
                          Print
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      }
    </div>
  );
};

export default PatientVisitView;
